/**
 * Tarif kaliti / API tenant obunasi — modal va obuna sahifasida bir xil ko‘rinish.
 */

export type TariffRow = {
  id: number;
  type_key?: string | null;
  custom_name?: string | null;
  monthly_price?: number;
  sort_order?: number;
};

export function planKeyFromTenant(tenant: Record<string, unknown> | null): string {
  if (!tenant) return '';
  return String(
    (tenant as { planKey?: string; plan_key?: string }).planKey ??
      (tenant as { plan_key?: string }).plan_key ??
      '',
  ).trim();
}

/** API `status` (active | trial | suspended) — `adminTenant_status_*` tarjimalari orqali. */
export function tenantSubscriptionStatusLabel(raw: unknown, t: (key: string) => string): string {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return '—';
  const key = `adminTenant_status_${s}`;
  const lbl = t(key);
  return lbl !== key ? lbl : String(raw ?? '—');
}

/**
 * Joriy tarif nomi: API ro‘yxati (custom_name / tariff_type_*) yoki to‘g‘ri kalit.
 */
export function displayNameForPlanKey(planKey: string, tariffs: TariffRow[], t: (k: string) => string): string {
  const k = String(planKey || '').trim();
  if (!k) return '—';
  const kLow = k.toLowerCase();
  const byId = /^\d+$/.test(k) ? tariffs.find((r) => String(r.id) === k) : undefined;
  const byType = tariffs.find((r) => r.type_key && r.type_key === k);
  // case-insensitive custom_name (e.g. plan_key="start" matches custom_name="Start")
  const byName = tariffs.find((r) => r.custom_name && String(r.custom_name).toLowerCase() === kLow);
  const row = byId ?? byType ?? byName;
  if (row) {
    if (row.custom_name && String(row.custom_name).trim() !== '') {
      return String(row.custom_name).trim();
    }
    if (row.type_key) {
      const trKey = `tariff_type_${row.type_key}`;
      const tr = t(trKey);
      if (tr && tr !== trKey) return tr;
      return String(row.type_key);
    }
  }
  const candidates = [`tariff_type_${k}`, k, `tariff_type_${kLow}`];
  for (const key of candidates) {
    const tr = t(key);
    if (tr && tr !== key) return tr;
  }
  if (k === 'start') {
    const tr = t('tariff_type_start');
    if (tr && tr !== 'tariff_type_start') return tr;
  }
  return k.charAt(0).toUpperCase() + k.slice(1);
}

/** Joriy tarif bo‘yicha oylik narx — API ro‘yxatidan (admin tariflar bilan mos). */
export function monthlyPriceForPlanKey(
  planKey: string,
  tariffs: TariffRow[],
  apiMonthlyPrice?: number,
): number {
  const k = String(planKey || '').trim();
  const fromApi = Math.max(0, Math.round(Number(apiMonthlyPrice) || 0));
  if (!k || tariffs.length === 0) {
    return fromApi;
  }
  const kLow = k.toLowerCase();
  const byId = /^\d+$/.test(k) ? tariffs.find((r) => String(r.id) === k) : undefined;
  const byType = tariffs.find((r) => r.type_key && r.type_key === k);
  const byName = tariffs.find((r) => r.custom_name && String(r.custom_name).toLowerCase() === kLow);
  const row = byId ?? byType ?? byName;
  const fromTariff = row ? Math.max(0, Math.round(Number(row.monthly_price) || 0)) : 0;
  if (fromTariff > 0) return fromTariff;
  return fromApi;
}

const TARIFF_COLUMN_RE = /tarif|plan_key|type_key|^pk$/i;
const NUMERIC_COLUMN_RE = /soni|count|cnt|miqdor|jami|qiymat|number|total/i;

/** AI chat SQL qatoridagi tarif ustunidagi qiymatni lokalizatsiya qiladi (sonli ustunlarga tegmaydi). */
export function localizeAdvisorSqlCell(columnLabel: string, rawValue: string, t: (k: string) => string): string {
  const val = String(rawValue ?? '').trim();
  if (!val || val === '—') return val;
  if (/^-?\d+(\.\d+)?$/.test(val.replace(/\s/g, ''))) return val;
  const col = String(columnLabel ?? '').trim();
  if (NUMERIC_COLUMN_RE.test(col) && !TARIFF_COLUMN_RE.test(col)) return val;
  if (!TARIFF_COLUMN_RE.test(col)) return val;
  return displayNameForPlanKey(val, [], t);
}
